import { buildApiUrl } from '../config/api';
import { notifyResearchAccessChange } from './researchAccessEvents';
import type {
  ComparisonModelCapabilities,
  ComparisonModelLane,
  ComparisonModelOption,
  ComparisonModelSelection,
  ComparisonModelSelections,
  ResearchComparisonCapabilities,
  ResearchComparisonEvent,
  ResearchProvenance,
  SafeEngineDiagnostic,
  SafeEngineErrorCategory,
  SafeEngineProviderCode,
  ResearchTimings,
  ResearchVariant,
  ResearchVariantCapability,
  ResearchVariantResult,
  ResearchComparisonFeedback,
  SavedResearchComparisonFeedback,
  SavedResearchComparisonRun,
  SavedResearchComparisonSummary,
  VariantEvaluation,
  VariantRunStatus,
} from '../types/localResearchComparison';

const CAPABILITIES_ENDPOINT = '/api/ris/compare/capabilities';
const SESSION_FROM_COMPARISON_ENDPOINT = '/api/ris/sessions/from-comparison';
const COMPARE_ENDPOINT = '/api/ris/compare';
const COMPARE_RUNS_ENDPOINT = `${COMPARE_ENDPOINT}/runs`;

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isVariant = (value: unknown): value is ResearchVariant => value === 'A' || value === 'B';
const asString = (value: unknown, fallback = ''): string => typeof value === 'string' ? value : fallback;
const asNumber = (value: unknown, fallback = 0): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;
const asNullableNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;
const asNullableString = (value: unknown): string | null => typeof value === 'string' && value.length > 0 ? value : null;

const SAFE_ERROR_CATEGORIES: readonly SafeEngineErrorCategory[] = [
  'UPSTREAM_TIMEOUT',
  'UPSTREAM_RATE_LIMIT',
  'UPSTREAM_CONTEXT_LIMIT',
  'PROVIDER_AUTH_FAILED',
  'MODEL_UNAVAILABLE',
  'UPSTREAM_NETWORK',
  'UPSTREAM_UNAVAILABLE',
  'UPSTREAM_RESPONSE_INVALID',
  'TOOL_PIPELINE_FAILED',
  'EMITTER_CONTRACT_FAILED',
  'RESULT_INVALID',
  'ENGINE_FAILED',
];

const SAFE_PROVIDER_CODES: readonly SafeEngineProviderCode[] = [
  'RESOURCE_EXHAUSTED',
  'DEADLINE_EXCEEDED',
  'PERMISSION_DENIED',
  'UNAUTHENTICATED',
  'INVALID_ARGUMENT',
  'UNAVAILABLE',
  'INTERNAL',
];

const parseSafeEngineDiagnostic = (value: unknown, eventCode: string): SafeEngineDiagnostic | undefined => {
  if (!isObject(value)) return undefined;
  const category = value.category;
  const fingerprint = value.fingerprint;
  const origin = value.origin;
  if (
    typeof category !== 'string'
    || !SAFE_ERROR_CATEGORIES.includes(category as SafeEngineErrorCategory)
    || category !== eventCode
    || typeof fingerprint !== 'string'
    || !/^[a-f0-9]{16}$/.test(fingerprint)
    // Auch der Wurf-Pfad der Route zählt: sonst bliebe die Zeile ohne
    // Fehlerreferenz, obwohl der Lauf eine gespeicherte Diagnose trägt.
    || (origin !== 'native.emitError' && origin !== 'worker.catch' && origin !== 'route.catch')
  ) return undefined;

  const diagnostic: SafeEngineDiagnostic = {
    category: category as SafeEngineErrorCategory,
    fingerprint,
    origin,
  };
  if (Number.isInteger(value.upstreamStatus) && (value.upstreamStatus as number) >= 400 && (value.upstreamStatus as number) <= 599) {
    diagnostic.upstreamStatus = value.upstreamStatus as number;
  }
  if (typeof value.providerCode === 'string' && SAFE_PROVIDER_CODES.includes(value.providerCode as SafeEngineProviderCode)) {
    diagnostic.providerCode = value.providerCode as SafeEngineProviderCode;
  }
  return diagnostic;
};

const MODEL_PRESET = /^[a-z0-9][a-z0-9._-]{0,63}$/;

const isModelLane = (value: unknown): value is ComparisonModelLane =>
  value === 'gemini' || value === 'local' || value === 'cortecs';

/**
 * Die Modelle, die der Server zur Wahl stellt. Eine nicht freigeschaltete Bahn
 * kommt gar nicht erst in die Liste, und ein Modellschlüssel muss die
 * geschlossene Gestalt haben.
 */
export function parseComparisonModelCapabilities(value: unknown): ComparisonModelCapabilities {
  const models = isObject(value) ? value : {};
  const laneB = isObject(models.laneB) ? models.laneB : {};
  const laneC = isObject(models.laneC) ? models.laneC : {};
  const laneD = isObject(models.laneD) ? models.laneD : {};
  const enabled = laneD.enabled === true;
  const presets = enabled && Array.isArray(laneD.presets)
    ? laneD.presets.filter((item): item is string => typeof item === 'string' && MODEL_PRESET.test(item))
    : [];
  return {
    laneB: { enabled: laneB.enabled === true },
    laneC: { enabled: laneC.enabled === true },
    laneD: { enabled, presets },
  };
}

/**
 * Die Einträge des Auswahlfeldes einer Vergleichsseite, in fester Reihenfolge:
 * zuerst Gemini, dann das lokale Modell, dann je ein Cortecs-Modell. Eine
 * nicht freigeschaltete Bahn fehlt ganz -- sie wäre nicht wählbar.
 */
export function comparisonModelOptions(models: ComparisonModelCapabilities): ComparisonModelOption[] {
  const options: ComparisonModelOption[] = models.laneB.enabled
    ? [{ value: 'gemini', label: 'Gemini (Bahn B)', selection: { lane: 'gemini' } }]
    : [];
  if (models.laneC.enabled) {
    options.push({ value: 'local', label: 'Lokales Modell (Bahn C)', selection: { lane: 'local' } });
  }
  if (models.laneD.enabled) {
    for (const preset of models.laneD.presets) {
      options.push({
        value: `cortecs:${preset}`,
        label: `Cortecs: ${preset}`,
        selection: { lane: 'cortecs', preset },
      });
    }
  }
  return options;
}

/** Der Wert des Auswahlfeldes zu einer Wahl; ohne Wahl steht Gemini da. */
export function comparisonModelValue(selection: ComparisonModelSelection | undefined): string {
  if (!selection) return 'gemini';
  return selection.lane === 'cortecs' && selection.preset
    ? `cortecs:${selection.preset}`
    : selection.lane;
}

/**
 * Die Wahl zu einem Wert des Auswahlfeldes. Ein Wert, den die Liste des
 * Servers nicht kennt, bleibt ungültig. Niemals unbemerkt ein anderes Modell
 * anfordern, wenn sich die Freigabe inzwischen geändert hat.
 */
export function comparisonModelSelection(
  value: string,
  models: ComparisonModelCapabilities,
): ComparisonModelSelection | undefined {
  return comparisonModelOptions(models).find(item => item.value === value)?.selection;
}

/** Eine Modellwahl braucht nur Staging, nicht die Bereitschaft von Production. */
export function comparisonSelectionReady(
  variant: ResearchVariant,
  selection: ComparisonModelSelection | undefined,
  capabilities: ResearchComparisonCapabilities,
): boolean {
  return selection
    ? comparisonModelOptions(capabilities.models).some(option => option.value === comparisonModelValue(selection))
    : capabilities.variants.some(item => item.variant === variant && item.ready);
}

/** Beschriftung einer Seite im laufenden Vergleich: Bahn und, bei D, Modell. */
export function comparisonModelLabel(selection: ComparisonModelSelection | undefined): string {
  if (!selection || selection.lane === 'gemini') return 'Bahn B · Gemini';
  if (selection.lane === 'local') return 'Bahn C · lokales Modell';
  return selection.preset ? `Bahn D · Cortecs ${selection.preset}` : 'Bahn D · Cortecs';
}

const parseModelSelection = (value: unknown): ComparisonModelSelection | null => {
  if (!isObject(value) || !isModelLane(value.lane)) return null;
  if (value.lane !== 'cortecs') return { lane: value.lane };
  return typeof value.preset === 'string' && MODEL_PRESET.test(value.preset)
    ? { lane: 'cortecs', preset: value.preset }
    : null;
};

export const parseComparisonModelSelections = (value: unknown): ComparisonModelSelections => {
  if (!isObject(value)) return {};
  const models: ComparisonModelSelections = {};
  for (const variant of ['A', 'B'] as const) {
    const selection = parseModelSelection(value[variant]);
    if (selection) models[variant] = selection;
  }
  return models;
};

const parseCapability = (value: unknown): ResearchVariantCapability | null => {
  if (!isObject(value) || !isVariant(value.variant)) return null;
  return {
    variant: value.variant,
    label: asString(value.label, value.variant === 'A' ? 'CLAiRIS Production' : 'CLAiRIS Staging'),
    sourceCommit: asString(value.sourceCommit, 'unbekannt'),
    ready: value.ready === true,
    reason: typeof value.reason === 'string' ? value.reason : undefined,
    coverage: Array.isArray(value.coverage) ? value.coverage.filter((item): item is string => typeof item === 'string') : [],
  };
};

export async function getResearchComparisonCapabilities(signal?: AbortSignal): Promise<ResearchComparisonCapabilities> {
  const response = await fetch(buildApiUrl(CAPABILITIES_ENDPOINT), {
    credentials: 'include',
    headers: { Accept: 'application/json' },
    signal,
  });

  if (!response.ok) throw new Error(`Vergleichsstatus konnte nicht geladen werden (${response.status}).`);
  const payload: unknown = await response.json();
  if (!isObject(payload)) throw new Error('Der Vergleichsstatus hat ein ungültiges Format.');

  return {
    enabled: payload.enabled === true,
    ready: payload.ready === true,
    executionMode: payload.executionMode === 'sequential' ? 'sequential' : 'parallel',
    variants: Array.isArray(payload.variants)
      ? payload.variants.map(parseCapability).filter((item): item is ResearchVariantCapability => item !== null)
      : [],
    models: parseComparisonModelCapabilities(payload.models),
    reason: typeof payload.reason === 'string' ? payload.reason : undefined,
  };
}

const parseTimings = (value: unknown): ResearchTimings => {
  const timings = isObject(value) ? value : {};
  return {
    firstStatusMs: asNullableNumber(timings.firstStatusMs),
    firstAnswerMs: asNullableNumber(timings.firstAnswerMs),
    totalMs: asNullableNumber(timings.totalMs),
  };
};

/** Remove model-internal fields before data reaches UI state or a local export. */
export function omitHiddenModelData(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(omitHiddenModelData);
  if (!isObject(value)) return value;

  return Object.fromEntries(
    Object.entries(value)
      .filter(([key]) => !/(modelParts|thought|thinking|reasoning|analysis)/i.test(key))
      .map(([key, item]) => [key, omitHiddenModelData(item)]),
  );
}

const parseResult = (value: unknown): ResearchVariantResult | null => {
  if (!isObject(value)) return null;
  const provenanceValue = omitHiddenModelData(value.provenance);
  const provenance = isObject(provenanceValue) ? provenanceValue as ResearchProvenance : {};

  return {
    answer: asString(value.answer),
    workflow: Array.isArray(value.workflow) ? omitHiddenModelData(value.workflow) as unknown[] : undefined,
    sources: Array.isArray(value.sources) ? omitHiddenModelData(value.sources) as unknown[] : undefined,
    verifiedSources: Array.isArray(value.verifiedSources) ? omitHiddenModelData(value.verifiedSources) as unknown[] : undefined,
    reviewAnnotation: value.reviewAnnotation !== undefined ? omitHiddenModelData(value.reviewAnnotation) : undefined,
    researchRunId: asNullableString(value.researchRunId) ?? undefined,
    researchCompletion: value.researchCompletion !== undefined ? omitHiddenModelData(value.researchCompletion) : undefined,
    metadata: isObject(value.metadata) ? omitHiddenModelData(value.metadata) as ResearchVariantResult['metadata'] : undefined,
    timings: parseTimings(value.timings),
    provenance,
  };
};

const RUN_STATUSES: readonly VariantRunStatus[] = ['idle', 'queued', 'running', 'done', 'error', 'cancelled'];

const parsePreference = (value: unknown): ResearchComparisonFeedback['preference'] =>
  value === 'A' || value === 'B' || value === 'tie' ? value : null;

const parseEvaluation = (value: unknown): VariantEvaluation => {
  const record = isObject(value) ? value : {};
  const parseRating = (rating: unknown): number | null =>
    Number.isInteger(rating) && (rating as number) >= 1 && (rating as number) <= 5 ? rating as number : null;
  return {
    accuracy: parseRating(record.accuracy),
    relevance: parseRating(record.relevance),
    citations: parseRating(record.citations),
    notes: typeof record.notes === 'string' ? record.notes : '',
  };
};

const parseEvaluations = (value: unknown): ResearchComparisonFeedback['evaluations'] => {
  const record = isObject(value) ? value : {};
  return { A: parseEvaluation(record.A), B: parseEvaluation(record.B) };
};

const parseSavedSummary = (value: unknown): SavedResearchComparisonSummary | null => {
  if (!isObject(value) || typeof value.runId !== 'string' || typeof value.userQuery !== 'string' || typeof value.startedAt !== 'string') return null;
  return {
    runId: value.runId,
    userQuery: value.userQuery,
    status: asString(value.status, 'completed'),
    startedAt: value.startedAt,
    completedAt: asNullableString(value.completedAt),
    variants: Array.isArray(value.variants) ? value.variants.filter(isVariant) : [],
    preference: parsePreference(value.preference),
    feedbackUpdatedAt: asNullableString(value.feedbackUpdatedAt),
    feedbackRevision: Number.isInteger(value.feedbackRevision) ? value.feedbackRevision as number : 0,
  };
};

const parseSavedVariant = (value: unknown): SavedResearchComparisonRun['results'][ResearchVariant] => {
  if (!isObject(value)) return undefined;
  const persistedStatus = asString(value.status);
  const status: VariantRunStatus = ({
    pending: 'queued',
    running: 'running',
    completed: 'done',
    error: 'error',
    cancelled: 'cancelled',
    timed_out: 'error',
  } as Record<string, VariantRunStatus>)[persistedStatus]
    ?? (RUN_STATUSES.includes(persistedStatus as VariantRunStatus) ? persistedStatus as VariantRunStatus : 'error');
  const persistedResult = {
    answer: value.answer,
    workflow: value.workflow,
    sources: value.sources,
    verifiedSources: value.verifiedSources,
    reviewAnnotation: value.reviewAnnotation,
    researchRunId: value.researchRunId,
    researchCompletion: value.researchCompletion,
    metadata: value.metadata,
    timings: value.timings,
    provenance: value.provenance,
  };
  const hasResult = isObject(value.result)
    || typeof value.answer === 'string'
    || Array.isArray(value.workflow)
    || Array.isArray(value.sources)
    || Array.isArray(value.verifiedSources)
    || value.reviewAnnotation !== undefined
    || typeof value.researchRunId === 'string'
    || isObject(value.provenance);
  const parsedResult = hasResult ? parseResult(isObject(value.result) ? value.result : persistedResult) : null;
  const error = isObject(value.error) && typeof value.error.code === 'string' && typeof value.error.message === 'string'
    ? { code: value.error.code, message: value.error.message, diagnostic: parseSafeEngineDiagnostic(value.error.diagnostic, value.error.code) }
    : undefined;
  return {
    status,
    answer: asString(value.answer, parsedResult?.answer ?? ''),
    tools: Array.isArray(value.tools)
      ? omitHiddenModelData(value.tools) as unknown[]
      : Array.isArray(value.workflow) ? omitHiddenModelData(value.workflow) as unknown[] : parsedResult?.workflow ?? [],
    ...(parsedResult ? { result: parsedResult } : {}),
    ...(error ? { error } : {}),
    elapsedMs: asNullableNumber(value.elapsedMs ?? parsedResult?.timings.totalMs),
  };
};

async function readJsonResponse(response: Response, fallback: string): Promise<Record<string, unknown>> {
  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    throw new Error(fallback);
  }
  if (!response.ok) {
    throw new Error(isObject(payload) && typeof payload.error === 'string' ? payload.error : fallback);
  }
  if (!isObject(payload)) throw new Error(fallback);
  return payload;
}

export async function listResearchComparisonRuns(signal?: AbortSignal): Promise<SavedResearchComparisonSummary[]> {
  const response = await fetch(buildApiUrl(`${COMPARE_RUNS_ENDPOINT}?limit=20`), {
    credentials: 'include',
    headers: { Accept: 'application/json' },
    signal,
  });
  const payload = await readJsonResponse(response, `Gespeicherte Vergleiche konnten nicht geladen werden (${response.status}).`);
  const runs = Array.isArray(payload.runs) ? payload.runs : [];
  return runs.map(parseSavedSummary).filter((run): run is SavedResearchComparisonSummary => run !== null);
}

export async function getResearchComparisonRun(runId: string, signal?: AbortSignal): Promise<SavedResearchComparisonRun> {
  const response = await fetch(buildApiUrl(`${COMPARE_RUNS_ENDPOINT}/${encodeURIComponent(runId)}`), {
    credentials: 'include',
    headers: { Accept: 'application/json' },
    signal,
  });
  const payload = await readJsonResponse(response, `Der gespeicherte Vergleich konnte nicht geladen werden (${response.status}).`);
  const raw = isObject(payload.run) ? payload.run : payload;
  const summary = parseSavedSummary(raw);
  if (!summary) throw new Error('Der gespeicherte Vergleich hat ein ungültiges Format.');
  const rawResults = isObject(raw.results) ? raw.results : {};
  const results: SavedResearchComparisonRun['results'] = {};
  for (const variant of ['A', 'B'] as const) {
    const result = parseSavedVariant(rawResults[variant]);
    if (result) results[variant] = result;
  }
  const savedPreference = parsePreference(raw.preference);
  const preference = savedPreference === 'A' || savedPreference === 'B'
    ? summary.variants.includes(savedPreference) ? savedPreference : null
    : savedPreference;
  return {
    ...summary,
    results,
    evaluations: parseEvaluations(raw.evaluations),
    preference,
  };
}

export async function saveResearchComparisonFeedback(
  runId: string,
  feedback: ResearchComparisonFeedback,
  signal?: AbortSignal,
): Promise<SavedResearchComparisonFeedback> {
  const response = await fetch(buildApiUrl(`${COMPARE_RUNS_ENDPOINT}/${encodeURIComponent(runId)}/feedback`), {
    method: 'PUT',
    credentials: 'include',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(feedback),
    signal,
  });
  const payload = await readJsonResponse(response, `Die Bewertung konnte nicht gespeichert werden (${response.status}).`);
  const raw = isObject(payload.feedback) ? payload.feedback : payload;
  if (typeof raw.runId !== 'string' || typeof raw.feedbackUpdatedAt !== 'string') {
    throw new Error('Die Speicherbestätigung hat ein ungültiges Format.');
  }
  if (raw.runId !== runId) throw new Error('Die Speicherbestätigung gehört zu einem anderen Vergleich.');
  const saved: SavedResearchComparisonFeedback = {
    runId: raw.runId,
    feedbackUpdatedAt: raw.feedbackUpdatedAt,
    feedbackRevision: Number.isInteger(raw.feedbackRevision) ? raw.feedbackRevision as number : 0,
    evaluations: parseEvaluations(raw.evaluations),
    preference: parsePreference(raw.preference),
  };
  if (saved.feedbackRevision < 1 || JSON.stringify(saved.evaluations) !== JSON.stringify(feedback.evaluations) || saved.preference !== feedback.preference) {
    throw new Error('Die Datenbank hat die Bewertung nicht vollständig bestätigt.');
  }
  return saved;
}

/** Best-effort feedback-only flush when the comparison view is unmounted by SPA navigation. */
export function flushResearchComparisonFeedback(
  runId: string,
  feedback: ResearchComparisonFeedback,
): void {
  void fetch(buildApiUrl(`${COMPARE_RUNS_ENDPOINT}/${encodeURIComponent(runId)}/feedback`), {
    method: 'PUT',
    credentials: 'include',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(feedback),
    keepalive: true,
  }).catch(() => undefined);
}

export function parseResearchComparisonEvent(value: unknown): ResearchComparisonEvent | null {
  if (!isObject(value) || typeof value.type !== 'string') return null;

  switch (value.type) {
    case 'run.started': {
      if (typeof value.runId !== 'string' || typeof value.userQuery !== 'string') return null;
      return {
        type: 'run.started',
        runId: value.runId,
        userQuery: value.userQuery,
        startedAt: asString(value.startedAt, new Date().toISOString()),
        variants: Array.isArray(value.variants) ? value.variants.filter(isVariant) : [],
        models: parseComparisonModelSelections(value.models),
      };
    }
    case 'run.error':
      return (typeof value.runId === 'string' || value.runId === null) && typeof value.code === 'string' && typeof value.message === 'string'
        ? { type: value.type, runId: value.runId, code: value.code, message: value.message }
        : null;
    case 'variant.status':
      return isVariant(value.variant) ? { type: value.type, variant: value.variant, message: asString(value.message), elapsedMs: asNumber(value.elapsedMs) } : null;
    case 'variant.chunk':
      return isVariant(value.variant) ? { type: value.type, variant: value.variant, text: asString(value.text), elapsedMs: asNumber(value.elapsedMs) } : null;
    case 'variant.answer_reset':
      return isVariant(value.variant) && typeof value.text === 'string'
        ? { type: value.type, variant: value.variant, text: value.text, elapsedMs: asNumber(value.elapsedMs) }
        : null;
    case 'variant.tools': {
      if (!isVariant(value.variant)) return null;
      return { type: value.type, variant: value.variant, tools: omitHiddenModelData(value.tools), elapsedMs: asNumber(value.elapsedMs) };
    }
    case 'variant.done': {
      const result = parseResult(value.result);
      return isVariant(value.variant) && result ? { type: value.type, variant: value.variant, result, elapsedMs: asNumber(value.elapsedMs) } : null;
    }
    case 'variant.error': {
      if (!isVariant(value.variant)) return null;
      const code = asString(value.code, 'UNKNOWN');
      const diagnostic = parseSafeEngineDiagnostic(value.diagnostic, code);
      return {
        type: value.type,
        variant: value.variant,
        code,
        message: asString(value.message, 'Die Variante ist fehlgeschlagen.'),
        ...(diagnostic ? { diagnostic } : {}),
        elapsedMs: asNumber(value.elapsedMs),
      };
    }
    case 'run.done':
      return typeof value.runId === 'string' ? { type: value.type, runId: value.runId } : null;
    default:
      return null;
  }
}

function parseSseBlock(block: string): ResearchComparisonEvent | null {
  const data = block
    .split(/\r?\n/)
    .filter(line => line.startsWith('data:'))
    .map(line => line.slice(5).trimStart())
    .join('\n');
  if (!data || data === '[DONE]') return null;
  try {
    return parseResearchComparisonEvent(JSON.parse(data));
  } catch {
    return null;
  }
}

export async function runResearchComparison(
  request: {
    sessionId?: string;
    comparisonSessionId?: string;
    userQuery: string;
    variants: ResearchVariant[];
    models?: ComparisonModelSelections;
  },
  onEvent: (event: ResearchComparisonEvent) => void,
  signal: AbortSignal,
): Promise<void> {
  const response = await fetch(buildApiUrl(COMPARE_ENDPOINT), {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'text/event-stream', 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
    signal,
  });

  if (!response.ok) {
    let message = `Vergleich konnte nicht gestartet werden (${response.status}).`;
    try {
      const payload: unknown = await response.json();
      if (isObject(payload)) {
        notifyResearchAccessChange(response.status, payload.code);
        if (typeof payload.error === 'string') message = payload.error;
      }
    } catch { /* response body is optional */ }
    throw new Error(message);
  }
  if (!response.body) throw new Error('Der Vergleich liefert keinen auswertbaren Datenstrom.');

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    buffer = (buffer + decoder.decode(value, { stream: !done })).replace(/\r\n/g, '\n');
    const blocks = buffer.split('\n\n');
    buffer = blocks.pop() ?? '';
    blocks.forEach(block => {
      const event = parseSseBlock(block);
      if (event) onEvent(event);
    });
    if (done) break;
  }

  const finalEvent = parseSseBlock(buffer);
  if (finalEvent) onEvent(finalEvent);
}

/** Der aus einem Vergleich angelegte Chat. */
export interface ComparisonChatSession {
  sessionId: string;
}

/**
 * Legt aus einer fertigen Variante einen Chat an. Der Server setzt Frage und
 * Antwort als Nachrichten.
 */
export async function createChatSessionFromComparison(
  runId: string,
  variant: ResearchVariant,
  signal?: AbortSignal,
): Promise<ComparisonChatSession> {
  const response = await fetch(buildApiUrl(SESSION_FROM_COMPARISON_ENDPOINT), {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ runId, variant }),
    signal,
  });
  const payload = await readJsonResponse(
    response,
    `Die Recherche konnte nicht angelegt werden (${response.status}).`,
  );
  const sessionId = asString(payload.sessionId).trim();
  if (!sessionId) throw new Error('Die Antwort enthält keine Sitzung.');
  return { sessionId };
}

/**
 * Legt den Chat an und öffnet ihn über den Weg, den auch die Seitenleiste
 * nimmt. `onSessionCreated`
 * meldet die fertige Sitzung, bevor sie geöffnet wird: die Seitenleiste lädt
 * ihren Verlauf sonst erst nach der nächsten Frage nach.
 */
export async function openComparisonVariantInChat(
  runId: string,
  variant: ResearchVariant,
  open: (sessionId: string) => void,
  options: { signal?: AbortSignal; onSessionCreated?: (sessionId: string) => void } = {},
): Promise<void> {
  const session = await createChatSessionFromComparison(runId, variant, options.signal);
  options.onSessionCreated?.(session.sessionId);
  open(session.sessionId);
}
